<script setup lang="ts">
// Version history panel (inventory F15; reference markup M979–1001). Styles verbatim.
import { computed } from 'vue';
import { s, hv } from '@/app/templateHelpers';
import { t } from '@/app/i18n';
import type { MdVersion } from './api';
import { sizeLabel, versionNote, versionTime, type MdUser } from './format';

const props = defineProps<{
  versions: MdVersion[] | null;
  /** Version number being previewed (null = current). */
  selected: number | null;
  accent: string;
  userOf: (id: string) => MdUser;
  error?: string;
}>();
const emit = defineEmits<{ (e: 'select', n: number | null): void }>();

// Version 1 alone is the untouched upload: the reference shows the empty state until the first edit.
const rows = computed(() => {
  const vs = props.versions || [];
  if (vs.length <= 1) return [];
  return vs.map(v => {
    const u = props.userOf(v.updated_by || v.created_by), at = Date.parse(v.updated_at);
    const note = versionNote(v, vs);
    return {
      n: v.version_no, cur: v.is_current, time: versionTime(at), who: u.name, avatar: u.bg, initial: u.ini, note, size: sizeLabel(v.size),
      dot: v.is_current ? props.accent : 'var(--n45464b, #45464b)',
      bg: props.selected === v.version_no ? `color-mix(in oklch, ${props.accent} 30%, var(--n26272b, #26272b))` : 'transparent',
    };
  });
});
const pick = (r: { n: number; cur: boolean }) => emit('select', r.cur ? null : r.n);
</script>

<template>
<div role="region" :aria-label="t('Version history')" :style="s('margin-top:10px; padding:0 6px 6px; border-radius:calc(10px * var(--tp-round, 1)); background:var(--n26272b, #26272b); display:flex; flex-direction:column; gap:1px; max-height:240px; overflow-y:auto; scrollbar-width:none;')">
  <div :style="s('position:sticky; top:0; z-index:2; margin:0 -6px; padding:12px 14px 8px; font-size:13px; font-weight:700; color:var(--ne7e7ea, #e7e7ea); background:var(--n26272b, #26272b);')">{{ t('Version history') }}</div>
  <div v-for="r in rows" :key="r.n" role="button" tabindex="0" :aria-pressed="selected === r.n" :data-md-version="r.n" @click="pick(r)" @keydown.enter.prevent="pick(r)" @keydown.space.prevent="pick(r)" :style="s(`display:grid; grid-template-columns:14px max-content minmax(0, 1fr) max-content; align-items:center; column-gap:8px; height:32px; padding:0 8px; border-radius:calc(7px * var(--tp-round, 1)); background:${r.bg}; cursor:pointer; position:relative;`)" :class="[hv('background:var(--n2e2f33, #2e2f33);')]">
    <div :style="s(`width:7px; height:7px; border-radius:50%; background:${r.dot}; justify-self:center;`)"></div>
    <span :style="s('font-size:12px; color:var(--n9c9da3, #9c9da3); font-variant-numeric:tabular-nums; white-space:nowrap; min-width:104px;')">{{ r.time }}</span>
    <div :style="s('min-width:0; display:flex; align-items:center; gap:6px; overflow:hidden;')">
      <div aria-hidden="true" :style="s(`flex:0 0 18px; width:18px; height:18px; border-radius:50%; background:${r.avatar}; color:#fff; font-size:9px; font-weight:700; display:grid; place-items:center;`)">{{ r.initial }}</div>
      <span :style="s('font-size:13px; white-space:nowrap;')">{{ r.who }}</span>
      <span v-if="r.cur" :style="s(`flex-shrink:0; height:18px; padding:0 6px; border-radius:calc(999px * var(--tp-round, 1)); background:color-mix(in oklch, ${accent} 25%, transparent); color:${accent}; font-size:11px; font-weight:700; line-height:18px;`)">{{ t('Current') }}</span>
      <span v-if="r.note" :style="s('min-width:0; font-size:12px; color:var(--n8a8b91, #8a8b91); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;')">{{ r.note }}</span>
    </div>
    <span :style="s('font-size:12px; color:var(--n76777d, #76777d); font-variant-numeric:tabular-nums; text-align:right; white-space:nowrap;')">{{ r.size }}</span>
  </div>
  <div v-if="!rows.length && !error" :style="s('padding:6px 8px 8px; font-size:13px; color:var(--n76777d, #76777d);')">{{ t('No earlier versions yet. Edits are saved as versions.') }}</div>
  <div v-if="error" :style="s('padding:6px 8px 8px; font-size:13px; color:var(--n76777d, #76777d);')">{{ t('Version history needs a connection.') }}</div>
</div>
</template>
